import { useEffect } from 'react';
import type { ReactElement } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { Activity, Compass, FileText, FolderOpen, LayoutTemplate, Search, Settings, Users } from 'lucide-react';
import { CreditsIndicator, ThemeToggle, cn, useI18n } from '@xdocs/ui';
import { useCreditsBalance } from '../features/notifications/useCreditsBalance.js';
import { NotificationsBell } from '../features/notifications/NotificationsBell.js';
import { useWebT } from '../i18n/useWebT.js';
import { useSyncSettingsToServer } from '../ws/useSyncSettingsToServer.js';
import { useWs } from '../ws/WsProvider.js';
import { OfflineQueueIndicator } from '../pwa/OfflineQueueIndicator.js';
import { ConnectionBanner } from './ConnectionBanner.js';
import { AccountMenu } from './AccountMenu.js';
import styles from './AppShellLayout.module.css';

/**
 * Authenticated app frame — sidebar/topbar structure matches
 * `mockups-extracted/18-documents` pixel ground truth: brand mark (accent
 * "x" + dot) → sidebar nav (Documents/Explorer/Personas/Discover/Activity/
 * Settings, in that order) → topbar (search / credits pill / bell / theme
 * toggle / avatar menu). "Shares" and "Admin console" moved out of the
 * top-level sidebar into `<AccountMenu>` ("My shares" / role-gated "Admin
 * console") to match the mockup, which surfaces them there instead — both
 * routes stay fully reachable, just relocated, so no functionality is lost.
 *
 * The mockup's sidebar `Templates` item (`nav_templates`) now links to the
 * `/templates` gallery (F1 "Start faster" catalog — NOT bookforge document
 * Themes/F65, which stays a per-document styling setting).
 *
 * `<Outlet>` renders the matched child route (`/` → `DocumentsPage`, the
 * app's home per design/app/18-documents.html; more routes join this layout
 * as feature workstreams land).
 */
export function AppShellLayout(): ReactElement {
  const { t } = useI18n();
  const { t: tWeb } = useWebT();
  const navigate = useNavigate();
  const location = useLocation();
  const { balance, low, zero } = useCreditsBalance();
  const { connectionState } = useWs();
  // Already on /search — navigating there again is a same-route no-op (E15
  // dead-control audit: a click that changes nothing is indistinguishable
  // from a dead control). Guard both trigger paths and disable the button so
  // it's never a clickable control with zero observable effect.
  const onSearchPage = location.pathname === '/search';

  useSyncSettingsToServer();

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      const isSearchShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (!isSearchShortcut) return;
      event.preventDefault();
      if (onSearchPage) return;
      void navigate('/search');
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, onSearchPage]);

  return (
    <div className={cn(styles['shell'])}>
      <a href="#main-content" className={cn(styles['skipLink'])}>
        {t('common:nav.skipToContent')}
      </a>
      <ConnectionBanner />
      <div className={cn(styles['frame'])}>
        <aside className={cn(styles['sidebar'])} aria-label={tWeb('web:shell.nav.documents')}>
          <Link to="/" className={cn(styles['brand'])}>
            <span className={cn(styles['brandName'])}>
              <span className={cn(styles['brandAccent'])}>{/* i18n-exempt: brand name, never translated */}x</span>
              {/* i18n-exempt: brand name, never translated */}Docs
            </span>
            <span className={cn(styles['brandDot'])} aria-hidden="true" />
          </Link>
          <nav className={cn(styles['nav'])}>
            <NavLink to="/" end className={cn(styles['navItem'])}>
              <FileText size={16} aria-hidden="true" />
              {tWeb('web:shell.nav.documents')}
            </NavLink>
            <NavLink to="/explorer" className={cn(styles['navItem'])}>
              <FolderOpen size={16} aria-hidden="true" />
              {tWeb('web:shell.nav.explorer')}
            </NavLink>
            <NavLink to="/templates" className={cn(styles['navItem'])}>
              <LayoutTemplate size={16} aria-hidden="true" />
              {tWeb('web:shell.nav.templates')}
            </NavLink>
            <NavLink to="/personas" className={cn(styles['navItem'])}>
              <Users size={16} aria-hidden="true" />
              {tWeb('web:shell.nav.personas')}
            </NavLink>
            <NavLink to="/discover" className={cn(styles['navItem'])}>
              <Compass size={16} aria-hidden="true" />
              {tWeb('web:shell.nav.discover')}
            </NavLink>
            <NavLink to="/activity" className={cn(styles['navItem'])}>
              <Activity size={16} aria-hidden="true" />
              {tWeb('web:shell.nav.activity')}
            </NavLink>
            <NavLink to="/settings" className={cn(styles['navItem'])}>
              <Settings size={16} aria-hidden="true" />
              {tWeb('web:shell.nav.settings')}
            </NavLink>
          </nav>
          <div className={cn(styles['sidebarFooter'])}>
            <span className={cn(styles['connectionStatus'])} data-state={connectionState} role="status">
              <span className={cn(styles['connectionDot'])} aria-hidden="true" />
              {connectionState === 'offline'
                ? tWeb('web:connection.offline')
                : connectionState === 'reconnecting' || connectionState === 'connecting'
                  ? tWeb('web:connection.reconnecting')
                  : tWeb('web:connection.connected')}
            </span>
          </div>
        </aside>
        <div className={cn(styles['main'])}>
          <header className={cn(styles['topbar'])}>
            <button
              type="button"
              className={cn(styles['searchTrigger'])}
              disabled={onSearchPage}
              onClick={() => void navigate('/search')}
            >
              <Search size={16} aria-hidden="true" />
              <span className={cn(styles['searchLabel'])}>{tWeb('web:shell.search.placeholder')}</span>
              <span className={cn(styles['searchShortcut'])} aria-label={tWeb('web:shell.search.shortcutLabel')}>
                {/* i18n-exempt: platform keybinding glyph, not translated copy */}
                ⌘K
              </span>
            </button>
            <div className={cn(styles['topbarSpacer'])} />
            <div className={cn(styles['topbarControls'])}>
              {/* E20 §5.10-4 offline action queue chip — renders nothing
                  when the queue is empty (pwa/WIRING.md §2: "wherever makes
                  sense in the header/nav"). */}
              <OfflineQueueIndicator />
              <CreditsIndicator
                credits={balance}
                {...(balance === null ? {} : { formattedCredits: tWeb('web:credits.count', { count: balance }) })}
                label={tWeb('web:credits.label')}
                low={low}
                zero={zero}
                onClick={() => void navigate('/settings/billing')}
              />
              <NotificationsBell />
              <ThemeToggle label={tWeb('web:action.toggleTheme')} />
              <AccountMenu />
            </div>
          </header>
          <main id="main-content" className={cn(styles['content'])}>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
